import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/utils";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminTransactionsPage() {
    const navigate = useNavigate();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);

    const token = localStorage.getItem('superadmin_token');

    useEffect(() => {
        if (!token) {
            navigate('/superadmin/login');
            return;
        }
        loadTransactions();
    }, []);

    const loadTransactions = async () => {
        try {
            const response = await axios.get(`${API_BASE}/api/superadmin/transactions/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTransactions(response.data);
        } catch (error) {
            toast.error("Failed to load transactions");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background p-4 md:p-8">
            <div className="max-w-7xl mx-auto space-y-6">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate('/superadmin')}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </Button>
                    <div>
                        <h1 className="font-heading text-3xl font-bold">Subscription Transactions</h1>
                        <p className="text-muted-foreground">Platform-wide payment history</p>
                    </div>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>All Transactions</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {transactions.map((txn) => (
                                <div key={txn.id} className="flex items-center justify-between p-4 border rounded-lg">
                                    <div>
                                        <p className="font-medium">{txn.salon_name}</p>
                                        <p className="text-sm text-muted-foreground">{txn.salon_email}</p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Plan: {txn.plan_id} • Ref: {txn.reference}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            Paid: {new Date(txn.created_at).toLocaleString()}
                                        </p>
                                        {txn.subscription_end && (
                                            <p className="text-xs text-muted-foreground">
                                                Expires: {new Date(txn.subscription_end).toLocaleString()}
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <p className="font-medium text-lg">{formatCurrency(txn.amount / 100)}</p>
                                        <Badge variant={txn.status === 'success' ? 'default' : 'secondary'}>
                                            {txn.status}
                                        </Badge>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}